import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-lodash-debounce';

import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import { NearestCouriersCollection } from '../../../../../../types/api/hubs';
import FieldWithErrors from '../../../../../Forms/ReactHookForm/FieldWithErrors';

export function PickupAssignmentTab() {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const values = watch();

  const debouncedPickupPlaceId = useDebounce(
    values.pickup_address?.placeId,
    500
  );
  const debouncedPickupStreetName = useDebounce(
    values.pickup_address?.streetName,
    500
  );
  const debouncedPickupHouseNumber = useDebounce(
    values.pickup_address?.housenumber,
    500
  );

  const { data: nearestHub, isLoading: isNearestHubLoading } = useAxiosHook<{
    ime: string;
    id: number;
  }>(
    {
      url: '/hubs/nearest',
      data: {
        placeId: debouncedPickupPlaceId,
        addressFrom: `${debouncedPickupStreetName}, ${debouncedPickupHouseNumber}`,
      },
      method: 'POST',
    },
    {
      skipWhen: !debouncedPickupPlaceId,
    }
  );

  const { data, error, isLoading } = useAxiosHook<NearestCouriersCollection>(
    `/hubs/couriers?hub_id=${nearestHub?.id}`,
    {
      skipWhen: !nearestHub || !values?.pickupAssignmentCheckbox,
    }
  );

  const courierOptions = useMemo(
    () =>
      data?.map((courier) => ({
        label: `${courier.first_name} ${courier.last_name}`,
        value: courier.id,
      })) ?? [],
    [data]
  );

  return (
    <div className="pickup-assignment-container">
      <div className="pickup-assignment-checkbox">
        <FieldWithErrors
          name="pickupAssignmentCheckbox"
          label={false}
          className="p-d-flex p-mb-2 p-jc-between"
        >
          <Controller
            name="pickupAssignmentCheckbox"
            render={({ field }) => (
              <>
                <Checkbox
                  id="pickupAssignmentCheckbox"
                  name="pickupAssignmentCheckbox"
                  value={field.value}
                  checked={field.value}
                  onChange={field.onChange}
                />
                <label
                  htmlFor="pickupAssignmentCheckbox"
                  data-cy="pickupAssignmentCheckbox"
                >
                  {t('Assign to courier for reception')}
                </label>
              </>
            )}
          />
        </FieldWithErrors>
      </div>
      <FieldWithErrors name="pickupAssignmentCourier" label={false}>
        <Controller
          name="pickupAssignmentCourier"
          render={({ field }) => (
            <Dropdown
              id="pickupAssignmentCourier"
              name="pickupAssignmentCourier"
              options={courierOptions}
              value={field.value}
              onChange={field.onChange}
              filter
              showClear
              placeholder={
                isLoading || isNearestHubLoading
                  ? t('Loading...')
                  : t('Courier')
              }
              disabled={
                isLoading ||
                isNearestHubLoading ||
                !values?.pickupAssignmentCheckbox ||
                !!error
              }
              className="pickup-assignment-dropdown"
            />
          )}
        />
      </FieldWithErrors>
    </div>
  );
}
